import {
  BooleanField,
  BooleanInput,
  Create,
  Datagrid,
  DateField,
  Edit,
  EditButton,
  ImageField,
  List,
  ReferenceField,
  ReferenceInput,
  Resource,
  SelectInput,
  Show,
  SimpleForm,
  SimpleShowLayout,
  Tab,
  TabbedShowLayout,
  TextField,
  TextInput,
  AutocompleteInput,
  NumberField,
  NumberInput,
  FunctionField,
  NullableBooleanInput,
  minValue,
  maxValue
} from "react-admin";
import VersionedList, { DbVersionFilterInput, DbVersionField, DbVersionInput } from "../components/DbVersion";

// Muestra el boolean nullable is_functional_finishing como Y / N / - (null = sin responder)
const renderFunctionalFinishingYN = (record) => {
  if (record.is_functional_finishing === null || record.is_functional_finishing === undefined) return "-";
  return record.is_functional_finishing ? "Y" : "N";
};

// Componentes para cada tipo de proceso
// Extraction Process
const ExtractionFilters = [
  <TextInput
    key="extractionNameSearch"
    label="Search by name"
    source="name"
    alwaysOn
  />,
  <DbVersionFilterInput key="extractionDbVersion" alwaysOn />
];

const ExtractionList = (props) => (
  <VersionedList {...props} resource="extraction" filters={ExtractionFilters}>
    <div style={{ marginTop: '15px' }}>
      <Datagrid rowClick="show">
        <TextField source="id" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <TextField source="name" />
        <DbVersionField />
        <ReferenceField
          label="Raw material category"
          source="rawMaterialCategoryId"
          reference="rawMaterialCategory">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField
          label="Benchmark process"
          source="benchmarkProcessId"
          reference="process/extraction">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField
          label="Benchmark origin"
          source="benchmarkOriginId"
          reference="origins">
          <TextField source="name" />
        </ReferenceField>
        <BooleanField source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextField source="shortName" label="Short name" />
        <TextField source="functionalUnit" label="Functional unit" />
        <TextField source="recycled_or_reused_content" label="Recycled or reused content" />
        <TextField source="bio_based_content" label="Biobased content" />
        <TextField source="organic_content" label="Organic content" />
        <NumberField source="disaggregated_processes_count" label="Disaggregated Processes Count" />
        <TextField source="disaggregated_processes_list" label="Disaggregated Processes List" />
        <TextField source="ecobalyseTranslation" label="Ecobalyse" emptyText="-" />
        <TextField source="ecobalyse_extraction_proxy" label="Ecobalyse Extraction Proxy" emptyText="-" />
        <NumberField source="recyclability_score" label="Recyclability score" emptyText="-" />
        <TextField source="raw_material_type" label="Raw material type" emptyText="-" />
        <NumberField source="Ef_efficiency" label="EF efficiency" />
        <TextField source="Ef_justification" label="EF justification" emptyText="-" />
        <TextField source="Ef_source" label="EF source" emptyText="-" />
        <EditButton />
      </Datagrid>
    </div>
  </VersionedList>
);

const ShowExtraction = (props) => {
  return (
    <Show {...props} resource="extraction">
      <TabbedShowLayout>
        <Tab label="Main">
          <TextField source="id" />
          <TextField source="name" />
          <ReferenceField
            label="Raw material category"
            source="rawMaterialCategoryId"
            reference="rawMaterialCategory">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField
            label="Benchmark process"
            source="benchmarkProcessId"
            reference="process/extraction">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField
            label="Benchmark origin"
            source="benchmarkOriginId"
            reference="origins">
            <TextField source="name" />
          </ReferenceField>
          <BooleanField source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
          <TextField source="shortName" label="Short name" />
          <TextField source="functionalUnit" label="Functional unit" />
          <TextField source="recycled_or_reused_content" label="Recycled or reused content" />
          <TextField source="bio_based_content" label="Biobased content" />
          <TextField source="organic_content" label="Organic content" />
          <NumberField source="disaggregated_processes_count" label="Disaggregated Processes Count" />
          <TextField source="disaggregated_processes_list" label="Disaggregated Processes List" />
          <TextField source="ecobalyseTranslation" label="Ecobalyse" emptyText="-" />
          <TextField source="ecobalyse_extraction_proxy" label="Ecobalyse Extraction Proxy" emptyText="-" />
          <DateField source="createdAt" label="Creation date" />
          <DateField source="updatedAt" label="Update date" />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

const CreateExtraction = (props) => {
  return (
    <Create {...props} resource="extraction">
      <SimpleForm>
        <DbVersionInput />
        <TextInput source="name" />
        <ReferenceInput
          source="rawMaterialCategoryId"
          reference="rawMaterialCategory"
          label="Raw material category">
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput
          source="benchmarkProcessId"
          reference="process/extraction"
          label="Benchmark process"
          filterToQuery={searchText => ({ name: searchText })}>
          <AutocompleteInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput
          source="benchmarkOriginId"
          reference="origins"
          label="Benchmark origin"
          filterToQuery={searchText => ({ name: searchText })}>
          <AutocompleteInput optionText="name" />
        </ReferenceInput>
        <BooleanInput source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextInput source="shortName" label="Short name" />
        <TextInput source="functionalUnit" label="Functional unit" />
        <TextInput source="recycled_or_reused_content" label="Recycled or reused content" />
        <TextInput source="bio_based_content" label="Biobased content" />
        <TextInput source="organic_content" label="Organic content" />
        <NumberInput source="disaggregated_processes_count" label="Disaggregated Processes Count" />
        <TextInput source="disaggregated_processes_list" label="Disaggregated Processes List" />
        <TextInput source="ecobalyseTranslation" label="Ecobalyse" />
        <TextInput source="ecobalyse_extraction_proxy" label="Ecobalyse Extraction Proxy" />
        <NumberInput source="Ef_efficiency" label="EF efficiency" />
        <TextInput source="Ef_justification" label="EF justification" multiline fullWidth />
        <TextInput source="Ef_source" label="EF source" fullWidth />
      </SimpleForm>
    </Create>
  );
};

const EditExtraction = (props) => {
  return (
    <Edit {...props} resource="extraction">
      <SimpleForm>
        <DbVersionInput />
        <TextInput source="name" />
        <ReferenceInput
          source="rawMaterialCategoryId"
          reference="rawMaterialCategory"
          label="Raw material category">
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput
          source="benchmarkProcessId"
          reference="process/extraction"
          label="Benchmark process"
          filterToQuery={searchText => ({ name: searchText })}>
          <AutocompleteInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput
          source="benchmarkOriginId"
          reference="origins"
          label="Benchmark origin"
          filterToQuery={searchText => ({ name: searchText })}>
          <AutocompleteInput optionText="name" />
        </ReferenceInput>
        <BooleanInput source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextInput source="shortName" label="Short name" />
        <TextInput source="functionalUnit" label="Functional unit" />
        <TextInput source="recycled_or_reused_content" label="Recycled or reused content" />
        <TextInput source="bio_based_content" label="Biobased content" />
        <TextInput source="organic_content" label="Organic content" />
        <NumberInput source="disaggregated_processes_count" label="Disaggregated Processes Count" />
        <TextInput source="disaggregated_processes_list" label="Disaggregated Processes List" />
        <TextInput source="ecobalyseTranslation" label="Ecobalyse" />
        <TextInput source="ecobalyse_extraction_proxy" label="Ecobalyse Extraction Proxy" />
        <NumberInput source="recyclability_score" label="Recyclability score" step={0.01} validate={[minValue(0), maxValue(1)]} />
        <TextInput source="raw_material_type" label="Raw material type" />
        <NumberInput source="Ef_efficiency" label="EF efficiency" />
        <TextInput source="Ef_justification" label="EF justification" multiline fullWidth />
        <TextInput source="Ef_source" label="EF source" fullWidth />
      </SimpleForm>
    </Edit>
  );
};

// Processing Process
const ProcessingFilters = [
  <TextInput
    key="processingNameSearch"
    label="Search by name"
    source="name"
    alwaysOn
  />,
  <DbVersionFilterInput key="processingDbVersion" alwaysOn />
];

const ProcessingList = (props) => (
  <VersionedList {...props} resource="processing" filters={ProcessingFilters}>
    <div style={{ marginTop: '15px' }}>
      <Datagrid rowClick="show">
        <TextField source="id" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <TextField source="name" />
        <DbVersionField />
        <ReferenceField
          label="Category"
          source="categoryId"
          reference="lcaCategory">
          <TextField source="name" />
        </ReferenceField>
        <BooleanField source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextField source="functionalUnit" label="Functional unit" />
        <NumberField source="disaggregated_processes_count" label="Disaggregated Processes Count" />
        <TextField source="disaggregated_processes_list" label="Disaggregated Processes List" />
        <EditButton />
      </Datagrid>
    </div>
  </VersionedList>
);

const ShowProcessing = (props) => {
  return (
    <Show {...props}>
      <TabbedShowLayout>
        <Tab label="Main">
          <TextField source="id" />
          <TextField source="name" />
          <ReferenceField
            label="Category"
            source="categoryId"
            reference="lcaCategory">
            <TextField source="name" />
          </ReferenceField>
          <BooleanField source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
          <TextField source="functionalUnit" label="Functional unit" />
          <NumberField source="disaggregated_processes_count" label="Disaggregated Processes Count" />
          <TextField source="disaggregated_processes_list" label="Disaggregated Processes List" />
          <DateField source="createdAt" label="Creation date" />
          <DateField source="updatedAt" label="Update date" />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

const CreateProcessing = (props) => {
  return (
    <Create {...props}>
      <SimpleForm>
        <DbVersionInput />
        <TextInput source="name" />
        <ReferenceInput
          source="categoryId"
          reference="lcaCategory"
          label="Category">
          <SelectInput optionText="name" />
        </ReferenceInput>
        <BooleanInput source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextInput source="functionalUnit" label="Functional unit" />
        <NumberInput source="disaggregated_processes_count" label="Disaggregated Processes Count" validate={[minValue(0)]} />
        <TextInput source="disaggregated_processes_list" label="Disaggregated Processes List" multiline fullWidth />
      </SimpleForm>
    </Create>
  );
};

const EditProcessing = (props) => {
  return (
    <Edit {...props}>
      <SimpleForm>
        <DbVersionInput />
        <TextInput source="name" />
        <ReferenceInput
          source="categoryId"
          reference="lcaCategory"
          label="Category">
          <SelectInput optionText="name" />
        </ReferenceInput>
        <BooleanInput source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextInput source="functionalUnit" label="Functional unit" />
        <NumberInput source="disaggregated_processes_count" label="Disaggregated Processes Count" validate={[minValue(0)]} />
        <TextInput source="disaggregated_processes_list" label="Disaggregated Processes List" multiline fullWidth />
      </SimpleForm>
    </Edit>
  );
};

// Finishing Process
const FinishingFilters = [
  <TextInput
    key="finishingNameSearch"
    label="Search by name"
    source="name"
    alwaysOn
    resettable
  />,
  <DbVersionFilterInput key="finishingDbVersion" alwaysOn />
];

const FinishingList = (props) => (
  <VersionedList
    {...props}
    resource="finishing"
    filters={FinishingFilters}
  >
    <div style={{ marginTop: '15px' }}>
      <Datagrid rowClick="show">
        <TextField source="id" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <TextField source="name" />
        <DbVersionField />
        <ReferenceField
          label="Category"
          source="categoryId"
          reference="lcaCategory">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField
          label="Benchmark process"
          source="benchmarkProcessId"
          reference="process/finishing">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField
          label="Benchmark origin"
          source="benchmarkOriginId"
          reference="origins">
          <TextField source="name" />
        </ReferenceField>
        <BooleanField source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextField source="shortName" label="Short name" />
        <TextField source="functionalUnit" label="Functional unit" />
        <NumberField source="disaggregated_processes_count" label="Disaggregated Processes Count" />
        <TextField source="disaggregated_processes_list" label="Disaggregated Processes List" />
        <TextField source="ecobalyseTranslationPrint" label="Print Ecobalyse" emptyText="-" />
        <TextField source="ecobalyseTranslationTreatment" label="Fading Ecobalyse" emptyText="-" />
        <FunctionField source="is_functional_finishing" label="Functional finishing" render={renderFunctionalFinishingYN} />
        <EditButton />
      </Datagrid>
    </div>
  </VersionedList>
);

const ShowFinishing = (props) => {
  return (
    <Show {...props}>
      <TabbedShowLayout>
        <Tab label="Main">
          <TextField source="id" />
          <TextField source="name" />
          <ReferenceField
            label="Category"
            source="categoryId"
            reference="lcaCategory">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField
            label="Benchmark process"
            source="benchmarkProcessId"
            reference="process/finishing">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField
            label="Benchmark origin"
            source="benchmarkOriginId"
            reference="origins">
            <TextField source="name" />
          </ReferenceField>
          <BooleanField source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
          <TextField source="shortName" label="Short name" />
          <TextField source="functionalUnit" label="Functional unit" />
          <NumberField source="disaggregated_processes_count" label="Disaggregated Processes Count" />
          <TextField source="disaggregated_processes_list" label="Disaggregated Processes List" />
          <TextField source="ecobalyseTranslationPrint" label="Print Ecobalyse" emptyText="-" />
          <TextField source="ecobalyseTranslationTreatment" label="Fading Ecobalyse" emptyText="-" />
          <DateField source="createdAt" label="Creation date" />
          <DateField source="updatedAt" label="Update date" />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

const CreateFinishing = (props) => {
  return (
    <Create {...props}>
      <SimpleForm>
        <DbVersionInput />
        <TextInput source="name" />
        <ReferenceInput
          source="categoryId"
          reference="lcaCategory"
          label="Category">
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput
          source="benchmarkProcessId"
          reference="process/finishing"
          label="Benchmark process"
          filterToQuery={searchText => ({ name: searchText })}>
          <AutocompleteInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput
          source="benchmarkOriginId"
          reference="origins"
          label="Benchmark origin"
          filterToQuery={searchText => ({ name: searchText })}>
          <AutocompleteInput optionText="name" />
        </ReferenceInput>
        <BooleanInput source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextInput source="shortName" label="Short name" />
        <TextInput source="functionalUnit" label="Functional unit" />
        <NumberInput source="disaggregated_processes_count" label="Disaggregated Processes Count" validate={[minValue(0)]} />
        <TextInput source="disaggregated_processes_list" label="Disaggregated Processes List" multiline fullWidth />
        <TextInput source="ecobalyseTranslationPrint" label="Print Ecobalyse" />
        <TextInput source="ecobalyseTranslationTreatment" label="Fading Ecobalyse" />
      </SimpleForm>
    </Create>
  );
};

const EditFinishing = (props) => {
  return (
    <Edit {...props}>
      <SimpleForm>
        <DbVersionInput />
        <TextInput source="name" />
        <ReferenceInput
          source="categoryId"
          reference="lcaCategory"
          label="Category">
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput
          source="benchmarkProcessId"
          reference="process/finishing"
          label="Benchmark process"
          filterToQuery={searchText => ({ name: searchText })}>
          <AutocompleteInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput
          source="benchmarkOriginId"
          reference="origins"
          label="Benchmark origin"
          filterToQuery={searchText => ({ name: searchText })}>
          <AutocompleteInput optionText="name" />
        </ReferenceInput>
        <BooleanInput source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextInput source="shortName" label="Short name" />
        <TextInput source="functionalUnit" label="Functional unit" />
        <NumberInput source="disaggregated_processes_count" label="Disaggregated Processes Count" validate={[minValue(0)]} />
        <TextInput source="disaggregated_processes_list" label="Disaggregated Processes List" multiline fullWidth />
        <TextInput source="ecobalyseTranslationPrint" label="Print Ecobalyse" />
        <TextInput source="ecobalyseTranslationTreatment" label="Fading Ecobalyse" />
        <NullableBooleanInput source="is_functional_finishing" label="Functional finishing" />
      </SimpleForm>
    </Edit>
  );
};

// Manufacturing Process
const ManufacturingFilters = [
  <TextInput
    key="manufacturingNameSearch"
    label="Search by name"
    source="name"
    alwaysOn
    resettable
  />,
  <DbVersionFilterInput key="manufacturingDbVersion" alwaysOn />
];

const ManufacturingList = (props) => (
  <VersionedList
    {...props}
    resource="manufacturing"
    filters={ManufacturingFilters}
  >
    <div style={{ marginTop: '15px' }}>
      <Datagrid rowClick="show">
        <TextField source="id" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <TextField source="name" />
        <DbVersionField />
        <BooleanField source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextField source="functionalUnit" label="Functional unit" />
        <EditButton />
      </Datagrid>
    </div>
  </VersionedList>
);

const ShowManufacturing = (props) => {
  return (
    <Show {...props}>
      <TabbedShowLayout>
        <Tab label="Main">
          <TextField source="id" />
          <TextField source="name" />
          <BooleanField source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
          <TextField source="functionalUnit" label="Functional unit" />
          <DateField source="createdAt" label="Creation date" />
          <DateField source="updatedAt" label="Update date" />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

const CreateManufacturing = (props) => {
  return (
    <Create {...props}>
      <SimpleForm>
        <DbVersionInput />
        <TextInput source="name" />
        <BooleanInput source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextInput source="functionalUnit" label="Functional unit" />
      </SimpleForm>
    </Create>
  );
};

const EditManufacturing = (props) => {
  return (
    <Edit {...props}>
      <SimpleForm>
        <DbVersionInput />
        <TextInput source="name" />
        <BooleanInput source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextInput source="functionalUnit" label="Functional unit" />
      </SimpleForm>
    </Edit>
  );
};

// Packaging Process
const PackagingFilters = [
  <TextInput
    key="packagingNameSearch"
    label="Search by name"
    source="name"
    alwaysOn
  />,
  <DbVersionFilterInput key="packagingDbVersion" alwaysOn />
];

const PackagingList = (props) => (
  <VersionedList {...props} resource="packaging" filters={PackagingFilters}>
    <div style={{ marginTop: '15px' }}>
      <Datagrid rowClick="show">
        <TextField source="id" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <TextField source="name" />
        <DbVersionField />
        <BooleanField source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextField source="functionalUnit" label="Functional unit" />
        <EditButton />
      </Datagrid>
    </div>
  </VersionedList>
);

const ShowPackaging = (props) => {
  return (
    <Show {...props}>
      <TabbedShowLayout>
        <Tab label="Main">
          <TextField source="id" />
          <TextField source="name" />
          <BooleanField source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
          <TextField source="functionalUnit" label="Functional unit" />
          <DateField source="createdAt" label="Creation date" />
          <DateField source="updatedAt" label="Update date" />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

const CreatePackaging = (props) => {
  return (
    <Create {...props}>
      <SimpleForm>
        <DbVersionInput />
        <TextInput source="name" />
        <BooleanInput source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextInput source="functionalUnit" label="Functional unit" />
      </SimpleForm>
    </Create>
  );
};

const EditPackaging = (props) => {
  return (
    <Edit {...props}>
      <SimpleForm>
        <DbVersionInput />
        <TextInput source="name" />
        <BooleanInput source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextInput source="functionalUnit" label="Functional unit" />
      </SimpleForm>
    </Edit>
  );
};

// Transport Process
const TransportFilters = [
  <TextInput
    key="transportNameSearch"
    label="Search by name"
    source="name"
    alwaysOn
  />,
  <DbVersionFilterInput key="transportDbVersion" alwaysOn />
];

const TransportList = (props) => (
  <VersionedList {...props} resource="transport" filters={TransportFilters}>
    <div style={{ marginTop: '15px' }}>
      <Datagrid rowClick="show">
        <TextField source="id" />
        <TextField source="name" />
        <DbVersionField />
        <ReferenceField
          label="Transport Mode"
          source="transportModeId"
          reference="transportModes">
          <TextField source="transportMode" />
        </ReferenceField>
        <TextField source="shortName" label="Short name" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <BooleanField source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextField source="functionalUnit" label="Functional unit" />
        <ImageField source="icon" label="Icon" />
      </Datagrid>
    </div>
  </VersionedList>
);

const ShowTransport = (props) => (
  <Show {...props}>
    <SimpleShowLayout>
      <TextField source="id" />
      <TextField source="name" />
      <ReferenceField
          label="Transport Mode"
          source="transportModeId"
          reference="transportModes">
          <TextField source="transportMode" />
        </ReferenceField>
      <DateField source="createdAt" label="Creation date" />
      <DateField source="updatedAt" label="Update date" />
      <BooleanField source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
      <TextField source="shortName" label="Short name" />
      <TextField source="functionalUnit" label="Functional unit" />
      <ImageField source="icon" label="Icon" />
    </SimpleShowLayout>
  </Show>
);

const CreateTransport = (props) => {
  return (
    <Create {...props}>
      <SimpleForm>
        <DbVersionInput />
        <TextInput source="name" />
        <TextInput source="shortName" label="Short name" />
        <ReferenceInput
          source="transportModeId"
          reference="transportModes"
          label="Transport Mode">
          <SelectInput optionText="transportMode" />
        </ReferenceInput>
        <TextInput source="functionalUnit" label="Functional unit" />
        <BooleanInput source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextInput source="icon" label="Icon" />
      </SimpleForm>
    </Create>
  );
};

const EditTransport = (props) => {
  return (
    <Edit {...props}>
      <SimpleForm>
        <DbVersionInput />
        <TextInput source="name" />
        <TextInput source="category" />
        <TextInput source="shortName" label="Short name" />
        <ReferenceInput
          source="transportModeId"
          reference="transportModes"
          label="Transport Mode">
          <SelectInput optionText="transportMode" />
        </ReferenceInput>
        <TextInput source="functionalUnit" label="Functional unit" />
        <BooleanInput source="visibleInAppEcoCalculator" label="Visible in App Eco-calculator" />
        <TextInput source="icon" label="Icon" />
      </SimpleForm>
    </Edit>
  );
};

// Recursos individuales para cada tipo de proceso
const extractionResource = () => (
  <Resource
    name="extraction"
    options={{ label: 'Extraction', menuGroup: 'Bcome' }}
    list={ExtractionList}
    show={ShowExtraction}
    create={CreateExtraction}
    edit={EditExtraction}
    path="process/extraction"
  />
);

const processingResource = () => (
  <Resource
    name="processing"
    options={{ label: 'Processing', menuGroup: 'Bcome' }}
    list={ProcessingList}
    show={ShowProcessing}
    create={CreateProcessing}
    edit={EditProcessing}
    path="process/processing"
  />
);

const finishingResource = () => (
  <Resource
    name="finishing"
    options={{ label: 'Finishing', menuGroup: 'Bcome' }}
    list={FinishingList}
    show={ShowFinishing}
    create={CreateFinishing}
    edit={EditFinishing}
    path="process/finishing"
  />
);

const manufacturingResource = () => (
  <Resource
    name="manufacturing"
    options={{ label: 'Manufacturing', menuGroup: 'Bcome' }}
    list={ManufacturingList}
    show={ShowManufacturing}
    create={CreateManufacturing}
    edit={EditManufacturing}
    path="process/manufacturing"
  />
);

const packagingResource = () => (
  <Resource
    name="packaging"
    options={{ label: 'Packaging', menuGroup: 'Bcome' }}
    list={PackagingList}
    show={ShowPackaging}
    create={CreatePackaging}
    edit={EditPackaging}
    path="process/packaging"
  />
);

const transportResource = () => (
  <Resource
    name="transport"
    options={{ label: 'Transport', menuGroup: 'Bcome' }}
    list={TransportList}
    show={ShowTransport}
    create={CreateTransport}
    edit={EditTransport}
    path="process/transport"
  />
);

export {
  extractionResource, finishingResource,
  manufacturingResource, packagingResource, processingResource, transportResource
};

